import React, { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Search, X, Clock } from "lucide-react";
import { SearchBarMobileProps } from "../types";

const SearchBarMobile: React.FC<SearchBarMobileProps> = ({
  searchQuery,
  setSearchQuery,
  handleSearch,
  isSearchVisible,
  setIsSearchVisible,
}) => {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [recentSearches, setRecentSearches] = useState<string[]>([
    "iPhone 15",
    "Nike Air Max",
    "MacBook Air",
  ]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      handleSearch(e);
      if (!recentSearches.includes(searchQuery.trim())) {
        setRecentSearches((prev) => [searchQuery.trim(), ...prev.slice(0, 4)]);
      }
      setIsSearchVisible(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery("");
    inputRef.current?.focus();
  };

  const removeRecent = (term: string) => {
    setRecentSearches((prev) => prev.filter((t) => t !== term));
  };

  // Quand l'utilisateur clique sur une recherche récente, on remplit le champ sans fermer
  const handleRecentClick = (term: string) => {
    setSearchQuery(term);
    // Ne pas fermer, l'utilisateur devra cliquer sur "Rechercher"
  };

  // Même chose pour la suggestion
  const handleSuggestionClick = (term: string) => {
    setSearchQuery(term);
  };

  return (
    <AnimatePresence>
      {isSearchVisible && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm"
            onClick={() => setIsSearchVisible(false)}
          />

          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 350 }}
            className="fixed inset-x-0 bottom-0 top-0 z-50 bg-white rounded-t-2xl shadow-xl flex flex-col"
          >
            <div className="px-4 pt-4 pb-2 border-b border-gray-100">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-semibold text-gray-900">Recherche</h2>
                <button
                  onClick={() => setIsSearchVisible(false)}
                  className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  ref={inputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Que cherchez-vous ?"
                  className="w-full pl-10 pr-10 py-3 bg-gray-50 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={clearSearch}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                  >
                    <X className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                  </button>
                )}
              </form>

              {/* Bouton de recherche explicite */}
              <button
                onClick={handleSubmit}
                className="w-full mt-3 py-2.5 bg-purple-600 text-white rounded-xl text-sm font-medium hover:bg-purple-700 transition-colors flex items-center justify-center gap-2"
              >
                <Search className="w-4 h-4" />
                Rechercher
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-6">
              {!searchQuery && recentSearches.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-gray-400" />
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Récentes
                      </span>
                    </div>
                    <button
                      onClick={() => setRecentSearches([])}
                      className="text-xs text-gray-400 hover:text-gray-600"
                    >
                      Tout effacer
                    </button>
                  </div>
                  <div className="space-y-2">
                    {recentSearches.map((term, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-xl"
                      >
                        <button
                          onClick={() => handleRecentClick(term)}
                          className="flex-1 text-left text-sm text-gray-700"
                        >
                          {term}
                        </button>
                        <button
                          onClick={() => removeRecent(term)}
                          className="p-1 hover:bg-gray-200 rounded-full"
                        >
                          <X className="w-3.5 h-3.5 text-gray-400" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {searchQuery && (
                <div>
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 block">
                    Suggestions
                  </span>
                  <button
                    onClick={() => handleSuggestionClick(searchQuery)}
                    className="w-full text-left p-3 bg-gray-50 rounded-xl text-sm text-gray-700 hover:bg-gray-100 transition"
                  >
                    {searchQuery}
                  </button>
                </div>
              )}

              {!searchQuery && (
                <button
                  onClick={() => {
                    navigate("/explorer");
                    setIsSearchVisible(false);
                  }}
                  className="w-full py-2.5 bg-gray-900 text-white rounded-xl text-sm font-medium hover:bg-gray-800 transition-colors"
                >
                  Explorer tous les produits
                </button>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default SearchBarMobile;